import { ApplicationElement, html } from '@effectivastudio/lit-components';

import '../date_finder_option/element.js';

import style from './style.lit.css';

import translations from './translations.js';

import debounce from 'lodash-es/debounce.js';

/**
 * Wrapper for Date finder option component with functionality for counting votes
 *
 * @prop resultsHidden - If present, avatars and results are hidden, message is displayed (optional)
 * @prop resultsLink - Link to detailed results
 * @prop totalCount - Total number of votes
 * @prop votingClosed - When true, disables voting and renders closed voting message
 *
 * @slot default - For Date finder option components
 */

export default class DateFinderOptionWrapper extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  static properties = {
    resultsHidden: { type: Boolean },
    resultsLink: { type: String },
    totalCount: { type: Number },
    votingClosed: { type: Boolean },
  };

  /** @internal */
  static translations = translations;

  /** @internal */
  get options() {
    return Array.from(this.querySelectorAll('bu-date-finder-option'));
  }

  /** @internal */
  get allUnchecked() {
    return this.options.every(o => !o.checked);
  }

  /** @internal */
  get currentUserVoted() {
    return this.options.some(o => o.checked);
  }

  connectedCallback() {
    super.connectedCallback();
    this.initialTotalCount = this.totalCount;
    this.initialCurrentUserVoted = this.currentUserVoted;
  }

  handleSlotChange() {
    this.options.forEach(o => {
      o.totalCount = this.totalCount;
      o.resultsHidden = this.resultsHidden;
      o.disabled = this.votingClosed;
    });
  }

  handleVoted(event) {
    const currentOption = event.target;

    const otherOptions = this.options.filter(o => o !== currentOption);
    const anyOtherOptionAlreadyChecked = otherOptions.some(o => o.checked);
    const cannotAttendOption = this.options.find(o => o.isCannotAttend);

    if (!anyOtherOptionAlreadyChecked && currentOption.checked) {
      this.totalCount += 1;
    }

    if (this.allUnchecked) {
      this.totalCount -= 1;
    }

    this.options.forEach(o => {
      o.totalCount = this.totalCount;
    });

    // If the current option is "cannot attend" and is checked, uncheck all other options
    if (currentOption.isCannotAttend && currentOption.checked) {
      otherOptions.forEach(o => {
        if (o.checked) o.removeUser();
      });
    }

    // If the current option is NOT "cannot attend" and is checked, uncheck all "cannot attend" options
    if (!currentOption.isCannotAttend && currentOption.checked) {
      cannotAttendOption.checked && cannotAttendOption.removeUser();
    }
  }

  debouncedHandleVote = debounce(this.handleVoted, 10);

  renderHiddenMessage() {
    return html`
      ${this.resultsHidden
        ? html`<div>${this.t('voting.afterward_results')}</div>`
        : null}
    `;
  }

  renderVotingClosedMessage() {
    return html`
      ${this.votingClosed ? html`<div>${this.t('voting.closed')}</div>` : null}
    `;
  }

  render() {
    return html`
      <slot
        @slotchange=${this.handleSlotChange}
        @date-finder-option-voted=${this.debouncedHandleVote}>
      </slot>

      <div class="card-note">
        <a class="total-date-voter-count" href=${this.resultsLink}>
          ${this.t('x_members_voted', { count: this.totalCount })}
        </a>

        ${this.renderVotingClosedMessage()} ${this.renderHiddenMessage()}
      </div>
    `;
  }
}

DateFinderOptionWrapper.registerTagName('bu-date-finder-option-wrapper');

customElements.get('bu-date-finder-option-wrapper') ||
  customElements.define(
    'bu-date-finder-option-wrapper',
    DateFinderOptionWrapper
  );
